"use client";

import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Column } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { NoTranslate } from "@faire/web--source/ui/lib/localization";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import * as React from "react";
import styled from "styled-components";

import { formatAddressFields } from "../addressSchema";

const AddressParagraph = styled(Typography)``;

export const Address: React.FC<{
  address: Partial<IAddress> | undefined;
  boldFirst?: boolean;
  className?: string;
}> = ({ address, boldFirst = true, className }) => {
  const { strictLocalize } = useStrictLocalization();

  if (!address || Object.values(address).length === 0) {
    return null;
  }

  const fields = formatAddressFields(
    address,
    strictLocalize,
    // eslint-disable-next-line @faire/no-inline-hoc-fc
    (fieldName: string, fieldValue: string) => (
      <UntranslatedField fieldName={fieldName} fieldValue={fieldValue} />
    )
  );

  if (fields.length === 0) {
    return null;
  }

  return (
    <Column className={className}>
      {fields.map((line, index) => (
        <AddressParagraph
          key={index}
          variant={
            index === 0 && boldFirst
              ? "paragraphSansMedium"
              : "paragraphSansRegular"
          }
          align="left"
        >
          {
            // This is necessary so the components will individually have a `key`.
            line.map((component, index) => (
              <React.Fragment key={index}>{component}</React.Fragment>
            ))
          }
        </AddressParagraph>
      ))}
    </Column>
  );
};

export namespace AddressComponents {
  export const ParagraphComponent = AddressParagraph;
}

const UntranslatedField: React.FC<{
  fieldName: string;
  fieldValue: string;
}> = ({ fieldName, fieldValue }) => {
  switch (fieldName) {
    case "name":
    case "address1":
    case "address2":
    case "postal_code":
      return <NoTranslate>{fieldValue}</NoTranslate>;
    default:
      return <>{fieldValue}</>;
  }
};
